
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import { ShoppingBag, Users, TrendingUp, DollarSign, Activity, ArrowUpRight, ArrowDownRight, Sparkles, Clock, Eye } from 'lucide-react';
import { Order } from '../../types';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    pendingOrders: 0,
    users: 0,
    totalRevenue: 0
  });
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);

  useEffect(() => {
    onValue(ref(db, 'products'), (snap) => setStats(prev => ({ ...prev, products: snap.exists() ? Object.keys(snap.val()).length : 0 })));

    onValue(ref(db, 'orders'), (snap) => {
      const data = snap.val();
      if (data) {
        const orders = (Object.values(data) as Order[]).filter(Boolean);
        const revenue = orders.filter(o => o && o.status !== 'cancelled').reduce((acc, o) => acc + (o.total || 0), 0);
        const pending = orders.filter(o => o && o.status !== 'delivered' && o.status !== 'cancelled').length;
        setStats(prev => ({ 
          ...prev, 
          orders: orders.length, 
          pendingOrders: pending, 
          totalRevenue: revenue 
        }));
        setRecentOrders(orders.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 5));
      }
    });

    onValue(ref(db, 'users'), (snap) => setStats(prev => ({ ...prev, users: snap.exists() ? Object.keys(snap.val()).length : 0 })));
  }, []);

  const cards = [
    { title: 'Bekleyen Sipariş', value: stats.pendingOrders, icon: Clock, color: 'bg-amber-600', trend: 'İlgilenilmesi Gereken' },
    { title: 'Ürün Sayısı', value: stats.products, icon: ShoppingBag, color: 'bg-stone-800', trend: 'Katalog Genişliği' },
    { title: 'Brüt Ciro', value: `₺${stats.totalRevenue.toLocaleString()}`, icon: DollarSign, color: 'bg-emerald-600', trend: 'Satış Başarısı' },
    { title: 'Kayıtlı Müşteri', value: stats.users, icon: Users, color: 'bg-indigo-600', trend: 'Kullanıcı Kitlesi' },
  ];

  return (
    <div className="p-8 space-y-10 h-full overflow-y-auto scrollbar-hide">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Panel Özeti</h1>
          <p className="text-stone-500 font-medium italic mt-1">Mağaza yönetim verileri</p>
        </div>
        <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-2xl border border-stone-100 shadow-sm text-xs font-bold text-stone-600">
          <Activity size={14} className="text-amber-600" />
          <span>Sistem: Aktif</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, i) => (
          <div key={i} className="bg-white p-8 rounded-[2.5rem] border border-stone-100 shadow-sm hover:shadow-xl transition-all group">
            <div className={`w-14 h-14 ${card.color} text-white rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform mb-6`}>
              <card.icon size={28} />
            </div>
            <div className="text-3xl font-black text-stone-900 mb-1">{card.value}</div>
            <div className="text-[10px] font-black text-stone-400 uppercase tracking-widest">{card.title}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm">
          <div className="flex items-center justify-between mb-10">
            <h3 className="text-xl font-black flex items-center text-stone-900">
              <Sparkles className="mr-3 text-amber-600" /> Son Siparişler
            </h3>
            <Link to="/admin/orders" className="text-xs text-amber-800 font-black uppercase tracking-wider hover:underline">Tümü</Link>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-black text-stone-400 uppercase tracking-[0.2em] border-b border-stone-50">
                  <th className="pb-4">Müşteri</th>
                  <th className="pb-4">Tutar</th>
                  <th className="pb-4">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {recentOrders.map((order) => (
                  <tr key={order.id} className="group">
                    <td className="py-5">
                      <p className="text-sm font-bold text-stone-900">
                        {order.customerInfo?.firstName || 'Bilinmeyen'} {order.customerInfo?.lastName || ''}
                      </p>
                    </td>
                    <td className="py-5 text-sm font-black text-amber-800">₺{(order.total || 0).toLocaleString()}</td>
                    <td className="py-5">
                      <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase ${order.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {order.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
                {recentOrders.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-10 text-center text-stone-300 italic">Sipariş yok.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-stone-900 text-white p-10 rounded-[3rem] shadow-2xl flex flex-col">
          <div className="flex items-center space-x-2 text-amber-500 mb-6">
            <Clock size={20} />
            <span className="text-xs font-black uppercase tracking-widest">Hızlı Erişim</span>
          </div>
          <h3 className="text-2xl font-serif font-bold mb-6">Sipariş Yönetimi</h3>
          <p className="text-stone-400 text-sm mb-10 leading-relaxed">Siparişleri güncelleyerek süreci yönetin.</p>
          <Link to="/admin/orders" className="mt-auto block w-full text-center bg-white/10 hover:bg-white/20 py-4 rounded-2xl text-xs font-bold transition-all">Siparişlere Git</Link>
        </div>
      </div> 
    </div>
  );
};

export default AdminDashboard;
